import React, { useState, useEffect } from 'react';
import {
  StyleSheet,
  View,
  Text,
  ScrollView,
  Platform,
  Image,
  ActivityIndicator,
  TouchableOpacity,
  Alert,
} from 'react-native';
import { 
  generateAndStorePDF, 
  generateAndDownloadPDF, 
  checkGotenbergAvailability 
} from '../../services/pdfGenerator';
import Svg, { Circle, Text as SvgText } from 'react-native-svg';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { SignatureDisplay } from '../ui/SignatureCanvas';
import { Vehicle, Driver } from '../../types';
import { 
  getVehicleReceivingFormByAssignmentHybrid,
  getVehicleDamagesByReceivingForm,
  getAllVehicleDamages,
  getVehicleDamagesByAssignment,
  createSampleDamageMarkers
} from '../../api/supabase';
import theme from '../../theme/index';

const LOGO_ASSET = require('../../../assets/logo-icon-only.svg');
const VEHICLE_DIAGRAM_ASSET = require('../../assets/tesla-model-s.jpeg');

interface DamageMarker {
  id: string;
  x: number;
  y: number;
  type: 'scratch' | 'dent' | 'missing' | 'broken';
  severity: 'minor' | 'moderate' | 'major';
  description?: string;
  photoUrl?: string; // Photo URL from Supabase Storage
  timestamp: string;
}

interface ReceivingHistoryDisplayProps {
  assignmentId: string;
  vehicle?: Vehicle;
  driver?: Driver;
  suffix?: 'A' | 'B'; // For partnership assignments: A = primary driver, B = secondary driver
}

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatDateForPrint = (dateString?: string | null) => {
  if (!dateString) return 'Not set';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return escapeHtml(String(dateString));
    }
    return escapeHtml(date.toLocaleDateString());
  } catch {
    return escapeHtml(String(dateString));
  }
};

const severityColorMap: Record<string, string> = {
  major: '#ff4444',
  moderate: '#ffaa00',
  minor: '#44aa44',
};

const buildDamageMarkersSvg = (damageMarkers: DamageMarker[]) => {
  if (!damageMarkers.length) {
    return '';
  }

  const circles = damageMarkers
    .map((marker, i) => {
      const color = severityColorMap[marker.severity] || '#44aa44'; // Use severity colors
      // Use the same coordinate system as screen form (viewBox 0 0 350 250)
      return `<circle cx="${marker.x}" cy="${marker.y}" r="8" fill="${color}" stroke="#000" stroke-width="2" opacity="0.95" />
        <text x="${marker.x}" y="${marker.y + 2}" text-anchor="middle" dominant-baseline="middle" fill="#fff" font-size="10" font-weight="700">${i + 1}</text>`;
    })
    .join('\n        ');

  // Use the same viewBox as screen form (350x250)
  return `<svg viewBox="0 0 350 250" preserveAspectRatio="xMidYMid meet" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 10;">
      ${circles}
    </svg>`;
};

const buildDamageListHtml = (damageMarkers: DamageMarker[]) => {
  if (!damageMarkers.length) {
    return '<div class="damage-empty">لا توجد أضرار مسجلة / No damages recorded</div>';
  }

  return damageMarkers
    .map((marker, i) => {
      const color = severityColorMap[marker.severity] || '#44aa44'; // Use severity colors
      const typeLabel = marker.type ? escapeHtml(marker.type) : 'Unknown';
      const severityLabel = marker.severity ? escapeHtml(marker.severity) : 'minor';
      const description = marker.description ? escapeHtml(marker.description) : 'No description';

      return `<div class="damage-item">
        <div class="damage-index" style="background: ${color};">${i + 1}</div>
        <div class="damage-info">
          <div class="damage-title">${typeLabel} (${severityLabel})</div>
          <div class="damage-description">${description}</div>
        </div>
      </div>`;
    })
    .join('\n      ');
};

// Helper function to convert image URLs to base64 for PDF embedding
const imageUrlToBase64 = async (url: string): Promise<string> => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Failed to convert image to base64:', error);
    return '';
  }
};

interface PrintTemplateParams {
  receivingForm: any;
  vehicle?: Vehicle;
  driver?: Driver;
  damageMarkers: DamageMarker[];
  isPartnership: boolean;
  assetUris: {
    logo: string;
    vehicleDiagram: string;
  };
}

const buildReceivingPrintHtml = ({
  receivingForm,
  vehicle,
  driver,
  damageMarkers,
  isPartnership,
  assetUris,
}: PrintTemplateParams) => {
  const logoUri = assetUris.logo || '';
  const diagramUri = assetUris.vehicleDiagram || '';

  const valueOrFallback = (value: any, fallback = 'N/A') =>
    value !== undefined && value !== null && value !== '' ? escapeHtml(String(value)) : fallback;

  const receivingNotes = valueOrFallback(
    receivingForm?.receiving_notes || receivingForm?.additional_notes || receivingForm?.delivery_notes,
    'لا توجد ملاحظات / No notes'
  ).replace(/\n/g, '<br/>');

  const driverSignature = receivingForm?.driver_signature || receivingForm?.primary_driver_signature || '';
  const secondaryDriverSignature = receivingForm?.secondary_driver_signature || ''; // ✅ NEW: Secondary driver signature
  const adminSignature = receivingForm?.admin_signature || receivingForm?.manager_signature || '';

  const damageMarkersSvg = buildDamageMarkersSvg(damageMarkers);
  const damageList = buildDamageListHtml(damageMarkers);

  const companyNameAr = 'الشارقة لحلول النقل';
  const companyNameEn = 'Sharjah Transport Solutions';

  const receivingDate = formatDateForPrint(receivingForm?.receiving_date || receivingForm?.assignment_date || receivingForm?.delivery_date);
  const receivingTime = valueOrFallback(
    receivingForm?.receiving_time || receivingForm?.assignment_time || receivingForm?.delivery_time,
    'N/A'
  );
  const fuelQuantity = valueOrFallback(
    receivingForm?.fuel_quantity || (receivingForm?.fuel_level ? `${receivingForm?.fuel_level}%` : ''),
    'N/A'
  );
  const vehicleNumber = valueOrFallback(
    receivingForm?.receiving_vehicle_number || vehicle?.licensePlate || receivingForm?.vehicle_license_plate,
    'N/A'
  );
  const makeModelYear = (() => {
    if (receivingForm?.receiving_make_model_year) return escapeHtml(String(receivingForm.receiving_make_model_year));
    if (vehicle?.makeModel && vehicle?.year) return escapeHtml(`${vehicle.makeModel} (${vehicle.year})`);
    if (vehicle?.makeModel) return escapeHtml(vehicle.makeModel);
    if (receivingForm?.vehicle_make_model) return escapeHtml(String(receivingForm.vehicle_make_model));
    return 'N/A';
  })();
  const mileage = valueOrFallback(
    receivingForm?.receiving_mileage || receivingForm?.current_mileage || receivingForm?.final_mileage || receivingForm?.initial_mileage,
    'N/A'
  );

  const driverName = (() => {
    if (driver?.name) return escapeHtml(driver.name);
    if (receivingForm?.driver_name) return escapeHtml(String(receivingForm.driver_name));
    if (receivingForm?.primary_driver_name) return escapeHtml(String(receivingForm.primary_driver_name));
    return 'Not available';
  })();
  
  const driverPhone = (() => {
    if (driver?.phone) return escapeHtml(driver.phone);
    if (receivingForm?.driver_phone) return escapeHtml(String(receivingForm.driver_phone));
    if (receivingForm?.primary_driver_phone) return escapeHtml(String(receivingForm.primary_driver_phone));
    return 'Not available';
  })();
  
  const licensePlate = (() => {
    if (vehicle?.licensePlate) return escapeHtml(vehicle.licensePlate);
    if (receivingForm?.vehicle_license_plate) return escapeHtml(String(receivingForm.vehicle_license_plate));
    if (receivingForm?.license_plate) return escapeHtml(String(receivingForm.license_plate));
    return 'Not available';
  })();

  const conditionValue = (primary?: string, fallback?: string) =>
    valueOrFallback(primary || fallback, 'جيد / Good');

  return `<!doctype html>
    <html lang="en">
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <title>Vehicle Receiving Form</title>
        <style>
          @page { size: A4; margin: 10mm; }
          * { box-sizing: border-box; }
          html, body { margin: 0; padding: 0; font-family: 'Segoe UI', Arial, sans-serif; background: #fff; color: #1f2933; }
          body { padding: 0; }
          .sheet { max-width: 100%; margin: 0 auto; background: #fff; border: none; box-shadow: none; border-radius: 0; overflow: hidden; }
          .header { display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; border-bottom: 2px solid #000; background: #fff; }
          .header-left { display: flex; align-items: center; gap: 12px; flex: 1; }
          .logo { width: 50px; height: 50px; border-radius: 50%; border: 2px solid #000; display: flex; align-items: center; justify-content: center; background: #fff; }
          .logo img { width: 38px; height: 38px; object-fit: contain; }
          .company-info { display: flex; flex-direction: column; gap: 2px; }
          .company-info .ar { font-size: 15px; font-weight: 700; color: #0f172a; }
          .company-info .en { font-size: 11px; color: #475569; }
          .header-type { text-align: center; flex: 1; }
          .header-type .ar { font-size: 18px; font-weight: 700; color: #000; }
          .header-type .en { font-size: 12px; color: #666; margin-top: 2px; letter-spacing: 0.3px; }
          .section { padding: 12px 16px; border-bottom: 1px solid #ddd; }
          .section:last-of-type { border-bottom: none; }
          .section-title { text-align: center; font-size: 15px; font-weight: 700; color: #000; margin-bottom: 10px; position: relative; }
          .section-title::after { content: ''; width: 60px; height: 2px; background: #000; display: block; margin: 6px auto 0; border-radius: 999px; }
          .info-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; }
          .info-item { display: flex; flex-direction: column; gap: 4px; }
          .info-label { font-size: 10px; font-weight: 600; color: #666; text-transform: uppercase; letter-spacing: 0.4px; text-align: right; }
          .info-value { padding: 8px 10px; background: #f5f5f5; border: 1px solid #ddd; border-radius: 6px; min-height: 38px; text-align: right; font-size: 12px; color: #000; font-weight: 500; }
          .receiving-layout { display: grid; grid-template-columns: 1.1fr 0.9fr; gap: 16px; }
          .diagram-card { background: #f5f5f5; border: 1px solid #ddd; border-radius: 12px; padding: 10px 12px; }
          .diagram-title { text-align: center; font-size: 13px; font-weight: 600; color: #000; margin-bottom: 8px; }
          .diagram-image { position: relative; border-radius: 8px; overflow: hidden; border: 1px solid #ccc; background: #fff; padding: 8px; display: flex; justify-content: center; align-items: center; min-height: 220px; }
          .diagram-image img { width: 100%; max-width: 300px; height: auto; display: block; position: relative; z-index: 1; }
          .diagram-image svg { position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px; width: calc(100% - 16px); height: calc(100% - 16px); max-width: 300px; margin: auto; pointer-events: none; z-index: 2; }
          .diagram-meta { margin-top: 6px; font-size: 10px; color: #64748b; text-align: center; font-style: italic; }
          .receiving-data-column { display: flex; flex-direction: column; gap: 10px; }
          .data-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
          .data-field { display: flex; flex-direction: column; gap: 4px; }
          .field-label { font-size: 10px; font-weight: 600; color: #666; text-align: right; }
          .field-value { padding: 7px 9px; background: #f5f5f5; border: 1px solid #ddd; border-radius: 6px; text-align: right; font-size: 11px; color: #000; min-height: 34px; display: flex; align-items: center; justify-content: flex-end; }
          .conditions-inline { margin-top: 6px; display: flex; flex-direction: column; gap: 6px; }
          .condition-row-inline { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
          .condition-inline { display: flex; flex-direction: column; gap: 3px; }
          .condition-label-inline { font-size: 9px; font-weight: 600; color: #666; text-align: right; }
          .condition-value-inline { padding: 6px 8px; background: #fff; border: 1px solid #ccc; border-radius: 5px; text-align: center; font-size: 10px; font-weight: 600; color: #000; }
          .damage-list { margin-top: 10px; display: flex; flex-direction: column; gap: 6px; }
          .damage-item { display: flex; gap: 8px; padding: 8px; background: #fff; border: 1px solid #ccc; border-radius: 8px; align-items: flex-start; }
          .damage-index { width: 22px; height: 22px; border-radius: 50%; color: #fff; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 11px; flex-shrink: 0; }
          .damage-info { flex: 1; }
          .damage-title { font-size: 11px; font-weight: 600; color: #000; }
          .damage-description { font-size: 10px; color: #666; margin-top: 2px; line-height: 1.3; }
          .damage-coordinates { font-size: 9px; color: #999; margin-top: 2px; font-family: monospace; }
          .damage-empty { padding: 10px; border-radius: 8px; border: 1px dashed #ccc; background: #f5f5f5; text-align: center; font-size: 11px; color: #666; }
          .notes-card { margin-top: 10px; background: #fff; border-radius: 8px; border: 1px solid #ddd; padding: 10px; }
          .notes-title { text-align: center; font-weight: 600; font-size: 11px; color: #000; margin-bottom: 8px; }
          .notes-body { font-size: 11px; line-height: 1.5; color: #000; text-align: right; min-height: 30px; }
          .conditions-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 12px; }
          .condition-item { background: #f8fafc; border-radius: 12px; padding: 12px 16px; border: 1px solid rgba(148, 163, 184, 0.3); display: flex; flex-direction: column; gap: 6px; }
          .condition-label { font-size: 12px; font-weight: 600; color: #475569; text-align: right; }
          .condition-value { text-align: center; font-size: 13px; font-weight: 600; color: #0f172a; background: #fff; border-radius: 8px; border: 1px solid rgba(148, 163, 184, 0.5); padding: 10px; }
          .signature-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; page-break-inside: avoid; }
          .signature-card { border: 1px solid #ddd; border-radius: 14px; overflow: hidden; background: #fff; }
          .signature-heading { padding: 10px; background: #000; color: #fff; font-weight: 600; text-align: center; font-size: 12px; }
          .signature-body { padding: 12px; min-height: 80px; display: flex; align-items: center; justify-content: center; }
          .signature-placeholder { font-size: 12px; color: #999; text-align: center; }
          .signature-image { max-width: 100%; max-height: 120px; object-fit: contain; }
          .footer-note { padding: 6px 12px; font-size: 8px; color: #999; text-align: center; background: #f5f5f5; border-top: 1px solid #ddd; page-break-inside: avoid; page-break-before: avoid; margin-top: -2px; }
          .print-actions { position: fixed; top: 16px; right: 16px; display: flex; gap: 8px; z-index: 1000; }
          .print-actions button { border: none; border-radius: 8px; padding: 10px 16px; font-weight: 600; cursor: pointer; box-shadow: 0 4px 12px rgba(15, 23, 42, 0.12); display: flex; align-items: center; gap: 6px; }
          .print-btn { background: #22c55e; color: #fff; }
          .close-btn { background: #ef4444; color: #fff; }
          @media print {
            body { padding: 0; background: #fff; }
            .sheet { box-shadow: none; border: none; margin: 0; border-radius: 0; }
            .print-actions { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="print-actions">
          <button class="print-btn" onclick="window.print()">Print</button>
          <button class="close-btn" onclick="window.close()">Close</button>
        </div>
        <div class="sheet">
          <div class="header">
            <div class="header-left">
              <div class="logo">
                ${logoUri ? `<img src="${logoUri}" alt="logo" />` : '<span style="font-size:22px;color:#000;font-weight:700;">STSC</span>'}
              </div>
              <div class="company-info">
                <div class="ar">${companyNameAr}</div>
                <div class="en">${companyNameEn}</div>
              </div>
            </div>
            <div class="header-type">
              <div class="ar">استمارة استلام المركبة</div>
              <div class="en">VEHICLE RECEIVING FORM</div>
            </div>
          </div>

          <div class="section">
            <div class="info-grid" style="grid-template-columns: repeat(4, 1fr);">
              <div class="info-item">
                <div class="info-label">اسم الشركة / Company Name</div>
                <div class="info-value">${companyNameAr} / ${companyNameEn}</div>
              </div>
              <div class="info-item">
                <div class="info-label">رقم اللوحة / License Plate</div>
                <div class="info-value">${licensePlate}</div>
              </div>
              <div class="info-item">
                <div class="info-label">اسم السائق / Driver Name</div>
                <div class="info-value">${driverName}</div>
              </div>
              <div class="info-item">
                <div class="info-label">رقم الهاتف / Phone Number</div>
                <div class="info-value">${driverPhone}</div>
              </div>
            </div>
          </div>

          <div class="receiving-layout">
            <div class="diagram-card">
              <div class="diagram-title">مخطط المركبة والأضرار / Vehicle Diagram & Damage</div>
              <div class="diagram-image">
                ${diagramUri ? `<img src="${diagramUri}" alt="Vehicle" />` : ''}
                <svg viewBox="0 0 350 250" preserveAspectRatio="xMidYMid meet">
                  ${damageMarkersSvg}
                </svg>
              </div>
              <div class="diagram-meta">عرض الأضرار المسجلة / Displaying Recorded Damage (${damageMarkers.length} damages found)</div>

              <div class="notes-card">
                <div class="notes-title">ملاحظات عامة عن حالة المركبة / General Notes on Vehicle Condition</div>
                <div class="notes-body">${receivingNotes}</div>
              </div>

              ${damageMarkers.length > 0 ? damageList : ''}
            </div>

            <div class="receiving-data-column">
              <div class="data-row">
                <div class="data-field">
                  <div class="field-label">تاريخ الاستلام / Receiving Date</div>
                  <div class="field-value">${receivingDate}</div>
                </div>
                <div class="data-field">
                  <div class="field-label">وقت الاستلام / Receiving Time</div>
                  <div class="field-value">${receivingTime}</div>
                </div>
              </div>

              <div class="data-row">
                <div class="data-field">
                  <div class="field-label">كمية الوقود / Fuel Quantity</div>
                  <div class="field-value">${fuelQuantity}</div>
                </div>
                <div class="data-field">
                  <div class="field-label">رقم المركبة / Vehicle Number</div>
                  <div class="field-value">${vehicleNumber}</div>
                </div>
              </div>

              <div class="data-row">
                <div class="data-field">
                  <div class="field-label">الماركة والموديل والسنة / Make • Model • Year</div>
                  <div class="field-value">${makeModelYear}</div>
                </div>
                <div class="data-field">
                  <div class="field-label">قراءة عداد المسافة (كم) / Mileage Reading (km)</div>
                  <div class="field-value">${mileage}</div>
                </div>
              </div>

              <div class="section-title" style="font-size:16px;margin:16px 0 12px 0;font-weight:600;">تقييم حالة المركبة عند الإرجاع / Return Condition Assessment</div>
              
              <div class="conditions-inline">
                <div class="condition-row-inline">
                  <div class="condition-item-inline">
                    <div class="condition-label-inline">النظافة الخارجية / Exterior Cleanliness</div>
                    <div class="condition-value-inline">${conditionValue(receivingForm?.receiving_cleanliness_external, receivingForm?.exterior_condition)}</div>
                  </div>
                  <div class="condition-item-inline">
                    <div class="condition-label-inline">النظافة الداخلية / Interior Cleanliness</div>
                    <div class="condition-value-inline">${conditionValue(receivingForm?.receiving_cleanliness_internal, receivingForm?.interior_condition)}</div>
                  </div>
                </div>

                <div class="condition-row-inline">
                  <div class="condition-item-inline">
                    <div class="condition-label-inline">الإطارات / Tires</div>
                    <div class="condition-value-inline">${conditionValue(receivingForm?.receiving_tire_condition, receivingForm?.tire_condition)}</div>
                  </div>
                  <div class="condition-item-inline">
                    <div class="condition-label-inline">مصابيح الإضاءة والإشارات / Lights & Signals</div>
                    <div class="condition-value-inline">${conditionValue(receivingForm?.receiving_lights_condition, receivingForm?.lights_condition)}</div>
                  </div>
                </div>

                <div class="condition-row-inline">
                  <div class="condition-item-inline">
                    <div class="condition-label-inline">الرافعة / Jack</div>
                    <div class="condition-value-inline">${conditionValue(receivingForm?.receiving_jack_condition)}</div>
                  </div>
                  <div class="condition-item-inline">
                    <div class="condition-label-inline">طفاية الحريق / Fire Extinguisher</div>
                    <div class="condition-value-inline">${conditionValue(receivingForm?.receiving_fire_extinguisher_condition)}</div>
                  </div>
                </div>

                <div class="condition-row-inline">
                  <div class="condition-item-inline">
                    <div class="condition-label-inline">الإطار الاحتياطي / Spare Tire</div>
                    <div class="condition-value-inline">${conditionValue(receivingForm?.receiving_spare_tire_condition)}</div>
                  </div>
                  <div class="condition-item-inline">
                    <div class="condition-label-inline">الأدوات / Tools</div>
                    <div class="condition-value-inline">${conditionValue(receivingForm?.receiving_tools_condition)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">التوقيعات / Signatures</div>
            <div class="signature-grid">
              <div class="signature-card">
                <div class="signature-heading">${isPartnership ? 'توقيع السائق الأول / Driver A Signature' : 'توقيع السائق / Driver Signature'}</div>
                <div class="signature-body">
                  ${driverSignature ? `<img class="signature-image" src="${driverSignature}" alt="Driver signature" />` : '<div class="signature-placeholder">لا يوجد توقيع / No signature</div>'}
                </div>
              </div>
              ${isPartnership && secondaryDriverSignature ? `
              <div class="signature-card">
                <div class="signature-heading">توقيع السائق الثاني / Driver B Signature</div>
                <div class="signature-body">
                  <img class="signature-image" src="${secondaryDriverSignature}" alt="Secondary driver signature" />
                </div>
              </div>
              ` : ''}
              <div class="signature-card">
                <div class="signature-heading">توقيع الشركة / Company Signature</div>
                <div class="signature-body">
                  ${adminSignature ? `<img class="signature-image" src="${adminSignature}" alt="Company signature" />` : '<div class="signature-placeholder">لا يوجد توقيع / No signature</div>'}
                </div>
              </div>
            </div>
          </div>

          <div class="footer-note">
            Generated for internal use • For any discrepancies, contact the Fleet Operations team.
          </div>
        </div>
        <script>
          window.addEventListener('load', () => {
            try { window.focus(); } catch (err) { console.error(err); }
          });
        </script>
      </body>
    </html>`;
};

const resolveAssetUri = (asset: any) => {
  if (!asset) return '';
  if (typeof asset === 'string') return asset;
  if (asset?.uri && typeof asset.uri === 'string') return asset.uri;
  if (asset?.default && typeof asset.default === 'string') return asset.default;
  if (typeof asset === 'number') {
    const imageModule = Image as any;
    if (typeof imageModule?.resolveAssetSource === 'function') {
      const resolved = imageModule.resolveAssetSource(asset);
      if (resolved?.uri) return resolved.uri;
    }
  }
  if (Array.isArray(asset) && asset.length > 0) {
    return resolveAssetUri(asset[0]);
  }
  return '';
};

export const ReceivingHistoryDisplay: React.FC<ReceivingHistoryDisplayProps> = ({
  assignmentId,
  vehicle,
  driver,
  suffix,
}) => {
  const [loading, setLoading] = useState(true);
  const [receivingForm, setReceivingForm] = useState<any>(null);
  const [damageMarkers, setDamageMarkers] = useState<DamageMarker[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isPartnership, setIsPartnership] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState<boolean>(false);

  const isWeb = Platform.OS === 'web';

  useEffect(() => {
    loadReceivingData();
  }, [assignmentId, suffix]);

  const loadReceivingData = async () => {
    try {
      setLoading(true);
      setError(null);

      // ✅ NEW: Check if this is a partnership assignment
      const { getAssignmentHistory } = await import('../../api/supabase');
      const allAssignments = await getAssignmentHistory();
      const assignment = allAssignments.find((a: any) => a.id === assignmentId);
      const isPartnershipAssignment = !!assignment?.secondary_driver_id;
      setIsPartnership(isPartnershipAssignment);
      
      console.log('🔍 Assignment check:', {
        assignmentId,
        hasSecondaryDriver: isPartnershipAssignment,
        secondaryDriverId: assignment?.secondary_driver_id,
        suffix
      });

      // Load receiving form with suffix support for partnerships
      const rawFormData = await getVehicleReceivingFormByAssignmentHybrid(assignmentId, suffix);
      console.log('Received form data:', rawFormData, suffix ? `(Suffix: ${suffix})` : '');
      console.log('Form data fields available:', rawFormData ? Object.keys(rawFormData) : 'No data');
      console.log('Assignment date/time fields:', {
        assignment_date: rawFormData?.assignment_date,
        assignment_time: rawFormData?.assignment_time,
        delivery_date: rawFormData?.delivery_date,
        delivery_time: rawFormData?.delivery_time,
        created_at: rawFormData?.created_at
      });
      
      // Map database fields to display fields
      const mappedFormData = rawFormData ? {
        ...rawFormData,
        // Properly map receiving date/time fields
        receiving_time: rawFormData.assignment_time || rawFormData.delivery_time,
        receiving_date: rawFormData.assignment_date || rawFormData.delivery_date,
        
        // Map fuel and mileage fields
        fuel_quantity: rawFormData.fuel_level ? `${rawFormData.fuel_level}%` : (rawFormData.fuel_quantity || 'N/A'),
        receiving_mileage: rawFormData.current_mileage || rawFormData.final_mileage || rawFormData.initial_mileage,
        
        // Map vehicle information - Note: receiving_vehicle_number doesn't exist in DB
        // Use vehicle prop passed from parent component
        receiving_make_model_year: vehicle ? `${vehicle.makeModel} (${vehicle.year})` : (rawFormData.vehicle_make_model || 'N/A'),
        
        // Map notes
        receiving_notes: rawFormData.receiving_notes || rawFormData.additional_notes || rawFormData.delivery_notes,
        
        // Map condition assessments - use actual field names from database
        receiving_cleanliness_external: rawFormData.exterior_condition || 'good',
        receiving_cleanliness_internal: rawFormData.interior_condition || 'good', 
        receiving_tire_condition: rawFormData.tire_condition || 'good',
        
        // Map equipment conditions
        receiving_jack_condition: rawFormData.has_spare_key ? 'جيد / Good' : 'غير متوفر / Not Available',
        receiving_fire_extinguisher_condition: rawFormData.has_fire_extinguisher ? 'جيد / Good' : 'غير متوفر / Not Available',
        receiving_spare_tire_condition: rawFormData.has_warning_triangle ? 'جيد / Good' : 'غير متوفر / Not Available',
        receiving_tools_condition: rawFormData.has_documents ? 'جيد / Good' : 'غير متوفر / Not Available',
        receiving_lights_condition: 'جيد / Good', // Default as this field might not exist in DB
      } : null;
      
      setReceivingForm(mappedFormData);

      // Load damage markers if form exists
      if (rawFormData?.id) {
        try {
          console.log('🔍 Attempting to load damages for receiving form ID:', rawFormData.id);
          const damages = await getVehicleDamagesByReceivingForm(rawFormData.id);
          console.log('✅ Loaded damage markers:', damages);
          setDamageMarkers(damages || []);
          
          // If no damages found, try alternative approaches
          if (!damages || damages.length === 0) {
            console.log('⚠️ No damages found for receiving form. Checking alternative methods...');
            
            // Check if there are any damages in the database at all
            try {
              const allDamages = await getAllVehicleDamages();
              console.log('🔍 Total damages in database:', allDamages.length);
              if (allDamages.length > 0) {
                console.log('📋 Sample damage records:', allDamages.slice(0, 2));
              }
            } catch (error) {
              console.error('❌ Error checking all damages:', error);
            }
            
            // Try loading damages by assignment ID if available
            if (assignmentId) {
              console.log('🔍 Trying to load damages by assignment ID:', assignmentId);
              try {
                const assignmentDamages = await getVehicleDamagesByAssignment(assignmentId);
                if (assignmentDamages && assignmentDamages.length > 0) {
                  console.log('✅ Found damages via assignment:', assignmentDamages.length);
                  setDamageMarkers(assignmentDamages);
                } else {
                  // If still no damages found, offer to create sample damages for testing
                  console.log('🔧 No damages found. You can create sample damage markers for testing.');
                  console.log('🔧 Call createSampleDamageMarkers("' + rawFormData.id + '") to add test damages.');
                  
                  // Uncomment the line below to automatically create sample damages for testing:
                  // await createSampleDamageMarkers(rawFormData.id);
                }
              } catch (error) {
                console.error('❌ Error loading damages by assignment:', error);
              }
            }
          }
        } catch (damageError) {
          console.error('❌ Error loading damage markers:', damageError);
          setDamageMarkers([]);
        }
      } else {
        console.log('⚠️ No receiving form ID available, cannot load damage markers');
      }
    } catch (err: any) {
      console.error('Error loading receiving data:', err);
      setError(err.message || 'Failed to load receiving form data');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePDF = async (saveToStorage: boolean = false) => {
    if (!isWeb) {
      Alert.alert('PDF Generation', 'PDF generation is only available on the web version of the app.');
      return;
    }

    if (!receivingForm) {
      Alert.alert('PDF Generation', 'Form data is still loading. Please try again in a moment.');
      return;
    }

    try {
      setPdfGenerating(true);
      console.log('📄 Generating PDF using Gotenberg...');

      // Convert assets to base64 for embedding in PDF
      console.log('🔄 Converting assets to base64...');
      const logoUrl = resolveAssetUri(LOGO_ASSET);
      const diagramUrl = resolveAssetUri(VEHICLE_DIAGRAM_ASSET);
      
      const [logoBase64, diagramBase64] = await Promise.all([
        imageUrlToBase64(logoUrl),
        imageUrlToBase64(diagramUrl),
      ]);

      console.log('✅ Assets converted:', {
        logo: logoBase64 ? 'Success' : 'Failed',
        diagram: diagramBase64 ? 'Success' : 'Failed',
      });

      // Build HTML with base64-encoded assets
      const printHtml = buildReceivingPrintHtml({
        receivingForm,
        // Use the vehicle/driver props available in this component
        vehicle: vehicle,
        driver: driver,
        damageMarkers,
        isPartnership,
        assetUris: {
          logo: logoBase64 || logoUrl,
          vehicleDiagram: diagramBase64 || diagramUrl,
        },
      });

      const fileName = `Receiving-Report-${receivingForm?.reference_number || assignmentId}-${Date.now()}`;

      if (saveToStorage) {
        // Generate and store in Supabase
        const result = await generateAndStorePDF({
          html: printHtml,
          fileName,
          storageBucket: 'receiving-forms',
          storagePath: 'pdfs',
        });

        if (result.success && result.pdfUrl) {
          Alert.alert(
            '✅ PDF Generated',
            `PDF has been saved to cloud storage and is available at:\n${result.pdfUrl}`,
            [
              { text: 'OK' },
              { text: 'Open PDF', onPress: () => window.open(result.pdfUrl, '_blank') }
            ]
          );
        } else {
          throw new Error(result.error || 'Failed to save PDF');
        }
      } else {
        // Generate and download directly
        await generateAndDownloadPDF(printHtml, fileName);
        Alert.alert('PDF Downloaded', 'The PDF has been downloaded to your device.');
      }
    } catch (error) {
      console.error('❌ PDF generation failed:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      // Check if it's a Gotenberg connectivity issue
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
        Alert.alert(
          '🔌 PDF Service Unavailable',
          'The PDF generation service is not running.\n\nTo fix this:\n\n1. Make sure Docker Desktop is running\n2. Open terminal and run: docker-compose up -d\n3. Wait a moment and try again\n\nOr use the "Print Form" button instead.',
          [{ text: 'OK' }]
        );
      } else {
        Alert.alert(
          '❌ PDF Generation Failed',
          `Could not generate PDF: ${errorMessage}\n\nPlease try again or use the "Print Form" button as an alternative.`,
          [{ text: 'OK' }]
        );
      }
    } finally {
      setPdfGenerating(false);
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Not set';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Invalid date';
    }
  };

  const handlePrint = () => {
    if (!isWeb) {
      Alert.alert('Print', 'Printing is only available on the web version of the app.');
      return;
    }

    if (!receivingForm) {
      Alert.alert('Print', 'Form data is still loading. Please try again in a moment.');
      return;
    }

    // Debug: Log all available data before printing
    console.log('📋 PRINT DEBUG - Available data:', {
      receivingForm: receivingForm ? Object.keys(receivingForm) : 'null',
      vehicle: vehicle ? { licensePlate: vehicle.licensePlate, makeModel: vehicle.makeModel, year: vehicle.year } : 'null',
      driver: driver ? { name: driver.name, phone: driver.phone } : 'null',
      damageMarkers: damageMarkers.length,
      isPartnership
    });

    const printHtml = buildReceivingPrintHtml({
      receivingForm,
      vehicle,
      driver,
      damageMarkers,
      isPartnership,
      assetUris: {
        logo: resolveAssetUri(LOGO_ASSET),
        vehicleDiagram: resolveAssetUri(VEHICLE_DIAGRAM_ASSET),
      },
    });

    const printWindow = window.open('', '_blank', 'width=900,height=1200,scrollbars=yes');
    if (!printWindow) {
      Alert.alert('Error', 'Popup blocked — please allow popups for this site to print the form');
      return;
    }

    printWindow.document.open();
    printWindow.document.write(printHtml);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={styles.loadingText}>Loading receiving form...</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>Error: {error}</Text>
      </View>
    );
  }

  if (!receivingForm) {
    return (
      <View style={styles.noDataContainer}>
        <Text style={styles.noDataText}>No receiving form found for this assignment</Text>
      </View>
    );
  }

  const renderVehicleDiagram = () => {
    const getMarkerColor = (severity: string) => {
      switch (severity) {
        case 'major': return '#ff4444';
        case 'moderate': return '#ffaa00';
        case 'minor': return '#44ff44';
        default: return '#44ff44';
      }
    };

    console.log('Rendering vehicle diagram with damage markers:', damageMarkers);

    return (
      <View style={styles.diagramContainer}>
        <Text style={styles.diagramTitle}>
          مخطط المركبة والأضرار / Vehicle Diagram & Damage
        </Text>
        
        <View style={styles.vehicleDiagram}>
          <View style={styles.imageContainer}>
            <TouchableOpacity style={styles.touchableImage} disabled={true}>
              <Image
                  source={VEHICLE_DIAGRAM_ASSET}
                style={styles.teslaImage}
                resizeMode="contain"
                onError={(error) => {
                  console.error('Failed to load vehicle diagram image:', error.nativeEvent.error);
                }}
              />
              <Svg style={styles.svgOverlay} viewBox="0 0 350 250">
                {damageMarkers.map((marker, index) => (
                  <React.Fragment key={marker.id}>
                    <Circle
                      cx={marker.x}
                      cy={marker.y}
                      r="8"
                      fill={getMarkerColor(marker.severity)}
                      stroke="#000"
                      strokeWidth="2"
                    />
                    <SvgText
                      x={marker.x}
                      y={marker.y + 2}
                      fontSize="10"
                      fill="#ffffff"
                      textAnchor="middle"
                      fontWeight="bold"
                    >
                      {index + 1}
                    </SvgText>
                  </React.Fragment>
                ))}
              </Svg>
            </TouchableOpacity>
          </View>

          {/* Instructions */}
          <Text style={styles.instructionText}>
            عرض الأضرار المسجلة / Displaying Recorded Damage ({damageMarkers.length} damages found)
          </Text>
        </View>

        {/* Damage List */}
        {damageMarkers.length > 0 && (
          <View style={styles.damageList}>
            <Text style={styles.damageListTitle}>قائمة الأضرار / Damage List:</Text>
            {damageMarkers.map((marker, index) => (
              <View key={marker.id} style={styles.damageItem}>
                <View style={styles.damageItemHeader}>
                  <View style={[styles.damageNumber, { backgroundColor: getMarkerColor(marker.severity) }]}>
                    <Text style={styles.damageNumberText}>{index + 1}</Text>
                  </View>
                  <View style={styles.damageInfo}>
                    <Text style={styles.damageItemText}>
                      {marker.type} - {marker.severity}
                    </Text>
                    {marker.description && (
                      <Text style={styles.damageDescription}>
                        {marker.description}
                      </Text>
                    )}
                    {marker.photoUrl && (
                      <View style={styles.attachmentIndicator}>
                        <Text style={styles.attachmentText}>📎 صورة مرفقة / Photo Attached</Text>
                      </View>
                    )}
                  </View>
                </View>
              </View>
            ))}
          </View>
        )}
        
        {/* Debug info when no damages */}
        {damageMarkers.length === 0 && (
          <View style={styles.damageList}>
            <Text style={styles.damageListTitle}>لا توجد أضرار مسجلة / No damages recorded</Text>
            <Text style={styles.instructionText}>
              Form ID: {receivingForm?.id || 'N/A'}
            </Text>
          </View>
        )}
      </View>
    );
  };

  return (
    <View style={styles.container} nativeID="receiving-history-container">
      {/* Print Button */}
      <View style={styles.printButtonContainer}>
        {isWeb ? (
          <TouchableOpacity style={styles.printButton} onPress={handlePrint}>
            <Text style={styles.printButtonText}>🖨️ Print Form</Text>
          </TouchableOpacity>
        ) : (
          <View style={styles.printUnavailable}>
            <Text style={styles.printUnavailableText}>
              Printing is currently available only on the web application.
            </Text>
          </View>
        )}
      </View>
      
      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        {/* Header with Logo */}
        <View style={styles.headerSection}>
          <View style={styles.headerContent}>
            <View style={styles.headerLeft}>
              <View style={styles.logoContainer}>
                <Image
                  source={LOGO_ASSET}
                  style={styles.logoImage}
                  resizeMode="contain"
                />
              </View>
              <View style={styles.companyInfo}>
                <Text style={styles.companyNameAr}>الشارقة لحلول النقل</Text>
                <Text style={styles.companyNameEn}>Sharjah Transport Solutions</Text>
              </View>
            </View>
            <View style={styles.headerCenter}>
              <Text style={styles.typeAr}>استلام المركبة</Text>
              <Text style={styles.typeEn}>Vehicle Receiving</Text>
            </View>
            <View style={styles.headerRight}>
            </View>
          </View>
        </View>

        {/* Basic Information */}
        <Card style={styles.section}>
          <View style={styles.basicInfoGrid}>
            <View style={styles.inputGroup}>
              <Text style={styles.labelText}>اسم الشركة / Company Name</Text>
              <View style={styles.readOnlyInput}>
                <Text style={styles.readOnlyText}>الشارقة لحلول النقل / Sharjah Transport Solutions</Text>
              </View>
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.labelText}>رقم اللوحة / License Plate</Text>
              <View style={styles.readOnlyInput}>
                <Text style={styles.readOnlyText}>{vehicle?.licensePlate || receivingForm?.vehicle_license_plate || receivingForm?.receiving_vehicle_number || 'Loading...'}</Text>
              </View>
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.labelText}>اسم السائق / Driver Name</Text>
              <View style={styles.readOnlyInput}>
                <Text style={styles.readOnlyText}>{driver?.name || receivingForm?.driver_name || 'Loading...'}</Text>
              </View>
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.labelText}>رقم الهاتف / Phone Number</Text>
              <View style={styles.readOnlyInput}>
                <Text style={styles.readOnlyText}>{driver?.phone || receivingForm?.driver_phone || 'Not available'}</Text>
              </View>
            </View>
          </View>
        </Card>

        {/* Vehicle Receiving Data */}
        <Card style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>
              بيانات استلام المركبة / Vehicle Receiving Data
            </Text>
          </View>
          
          <View style={styles.receivingContent}>
            <View style={styles.leftColumn}>
              {renderVehicleDiagram()}
              <View style={styles.notesContainer}>
                <Text style={styles.notesLabel}>
                  ملاحظات عامة عن حالة المركبة:{'\n'}
                  General Notes on Vehicle Condition:
                </Text>
                <View style={styles.notesDisplay}>
                  <Text style={styles.notesText}>
                    {receivingForm.receiving_notes || 'لا توجد ملاحظات / No notes'}
                  </Text>
                </View>
              </View>
            </View>
            
            <View style={styles.rightColumn}>
              <View style={styles.dateTimeRow}>
                <View style={styles.inputGroup}>
                  <Text style={styles.labelText}>تاريخ الاستلام / Receiving Date</Text>
                  <View style={styles.readOnlyInput}>
                    <Text style={styles.readOnlyText}>{formatDate(receivingForm.receiving_date)}</Text>
                  </View>
                </View>
                <View style={styles.inputGroup}>
                  <Text style={styles.labelText}>وقت الاستلام / Receiving Time</Text>
                  <View style={styles.readOnlyInput}>
                    <Text style={styles.readOnlyText}>{receivingForm.receiving_time || 'N/A'}</Text>
                  </View>
                </View>
              </View>

              <View style={styles.dateTimeRow}>
                <View style={styles.inputGroup}>
                  <Text style={styles.labelText}>كمية الوقود / Fuel Quantity</Text>
                  <View style={styles.readOnlyInput}>
                    <Text style={styles.readOnlyText}>{receivingForm.fuel_quantity || 'N/A'}</Text>
                  </View>
                </View>
                <View style={styles.inputGroup}>
                  <Text style={styles.labelText}>رقم المركبة / Vehicle Number</Text>
                  <View style={styles.readOnlyInput}>
                    <Text style={styles.readOnlyText}>{vehicle?.licensePlate || 'N/A'}</Text>
                  </View>
                </View>
              </View>

              <View style={styles.dateTimeRow}>
                <View style={styles.inputGroup}>
                  <Text style={styles.labelText}>الماركة والموديل والسنة / Make and Model and Year</Text>
                  <View style={styles.readOnlyInput}>
                    <Text style={styles.readOnlyText}>{receivingForm.receiving_make_model_year || 'N/A'}</Text>
                  </View>
                </View>
                <View style={styles.inputGroup}>
                  <Text style={styles.labelText}>قراءة عداد المسافة (كم) / Mileage Reading (km)</Text>
                  <View style={styles.readOnlyInput}>
                    <Text style={styles.readOnlyText}>{receivingForm.receiving_mileage || receivingForm.initial_mileage || 'N/A'}</Text>
                  </View>
                </View>
              </View>
              
              <View style={styles.conditionsContainer}>
                <Text style={styles.conditionsTitle}>تقييم حالة المركبة عند الإرجاع / Return Condition Assessment</Text>
                <View style={styles.conditionRow}>
                  <View style={styles.conditionItem}>
                    <Text style={styles.conditionLabel}>النظافة الخارجية / Exterior Cleanliness:</Text>
                    <View style={styles.conditionDisplay}>
                      <Text style={styles.conditionDisplayText}>{receivingForm.receiving_cleanliness_external || receivingForm.cleanliness_external || 'جيد / Good'}</Text>
                    </View>
                  </View>
                  <View style={styles.conditionItem}>
                    <Text style={styles.conditionLabel}>النظافة الداخلية / Interior Cleanliness:</Text>
                    <View style={styles.conditionDisplay}>
                      <Text style={styles.conditionDisplayText}>{receivingForm.receiving_cleanliness_internal || receivingForm.cleanliness_internal || 'جيد / Good'}</Text>
                    </View>
                  </View>
                </View>
                <View style={styles.conditionRow}>
                  <View style={styles.conditionItem}>
                    <Text style={styles.conditionLabel}>الإطارات / Tires:</Text>
                    <View style={styles.conditionDisplay}>
                      <Text style={styles.conditionDisplayText}>{receivingForm.receiving_tire_condition || receivingForm.tire_condition || 'جيد / Good'}</Text>
                    </View>
                  </View>
                  <View style={styles.conditionItem}>
                    <Text style={styles.conditionLabel}>مصابيح الإضاءة والإشارات / Lights & Signals:</Text>
                    <View style={styles.conditionDisplay}>
                      <Text style={styles.conditionDisplayText}>{receivingForm.receiving_lights_condition || receivingForm.lights_condition || 'جيد / Good'}</Text>
                    </View>
                  </View>
                </View>
                <View style={styles.conditionRow}>
                  <View style={styles.conditionItem}>
                    <Text style={styles.conditionLabel}>الرافعة / Jack:</Text>
                    <View style={styles.conditionDisplay}>
                      <Text style={styles.conditionDisplayText}>{receivingForm.receiving_jack_condition || receivingForm.jack_condition || 'جيد / Good'}</Text>
                    </View>
                  </View>
                  <View style={styles.conditionItem}>
                    <Text style={styles.conditionLabel}>طفاية الحريق / Fire Extinguisher:</Text>
                    <View style={styles.conditionDisplay}>
                      <Text style={styles.conditionDisplayText}>{receivingForm.receiving_fire_extinguisher_condition || receivingForm.fire_extinguisher_condition || 'جيد / Good'}</Text>
                    </View>
                  </View>
                </View>
                <View style={styles.conditionRow}>
                  <View style={styles.conditionItem}>
                    <Text style={styles.conditionLabel}>الإطار الاحتياطي / Spare Tire:</Text>
                    <View style={styles.conditionDisplay}>
                      <Text style={styles.conditionDisplayText}>{receivingForm.receiving_spare_tire_condition || receivingForm.spare_tire_condition || 'جيد / Good'}</Text>
                    </View>
                  </View>
                  <View style={styles.conditionItem}>
                    <Text style={styles.conditionLabel}>الأدوات / Tools:</Text>
                    <View style={styles.conditionDisplay}>
                      <Text style={styles.conditionDisplayText}>{receivingForm.receiving_tools_condition || receivingForm.tools_condition || 'جيد / Good'}</Text>
                    </View>
                  </View>
                </View>
              </View>
            </View>
          </View>
        </Card>

        {/* Signatures */}
        <Card style={styles.section}>
          <View style={styles.signatureSection}>
            <View style={styles.signatureRow}>
              <Card style={styles.signatureCard}>
                <View style={styles.signatureHeader}>
                  <Text style={styles.signatureTitle}>توقيع السائق / Driver Signature</Text>
                </View>
                <View style={styles.signatureContent}>
                  {(receivingForm.driver_signature || receivingForm.primary_driver_signature) ? (
                    <SignatureDisplay 
                      signature={receivingForm.driver_signature || receivingForm.primary_driver_signature}
                      onEdit={() => {}}
                      readOnly={true}
                    />
                  ) : (
                    <View style={styles.signatureBox}>
                      <Text style={styles.signaturePlaceholder}>لا يوجد توقيع / No signature</Text>
                      <Text style={styles.instructionText}>
                        Available signature fields: {Object.keys(receivingForm).filter(key => key.includes('signature')).join(', ') || 'none'}
                      </Text>
                    </View>
                  )}
                </View>
              </Card>

              <Card style={styles.signatureCard}>
                <View style={styles.signatureHeader}>
                  <Text style={styles.signatureTitle}>توقيع الشركة / Company Signature</Text>
                </View>
                <View style={styles.signatureContent}>
                  {(receivingForm.admin_signature || receivingForm.manager_signature) ? (
                    <SignatureDisplay 
                      signature={receivingForm.admin_signature || receivingForm.manager_signature}
                      onEdit={() => {}}
                      readOnly={true}
                    />
                  ) : (
                    <View style={styles.signatureBox}>
                      <Text style={styles.signaturePlaceholder}>لا يوجد توقيع / No signature</Text>
                      <Text style={styles.instructionText}>
                        Available signature fields: {Object.keys(receivingForm).filter(key => key.includes('signature')).join(', ') || 'none'}
                      </Text>
                    </View>
                  )}
                </View>
              </Card>
            </View>
          </View>
        </Card>

        {/* Print & PDF Buttons */}
        {Platform.OS === 'web' && (
          <View style={{ margin: 16, alignItems: 'center', gap: 12 }}>
            <Button
              title="طباعة النموذج / Print Form"
              onPress={handlePrint}
              variant="primary"
              style={{ minWidth: 200, paddingHorizontal: 24, paddingVertical: 12 }}
              disabled={loading || !receivingForm}
            />
            
            {/* PDF Generation Buttons */}
            <View style={{ flexDirection: 'row', gap: 12, marginTop: 8 }}>
              <Button
                title="📄 Download PDF"
                onPress={() => handleGeneratePDF(false)}
                variant="secondary"
                style={{ paddingHorizontal: 20, paddingVertical: 10 }}
                disabled={loading || !receivingForm || pdfGenerating}
              />
              <Button
                title="☁️ Save PDF to Cloud"
                onPress={() => handleGeneratePDF(true)}
                variant="secondary"
                style={{ paddingHorizontal: 20, paddingVertical: 10 }}
                disabled={loading || !receivingForm || pdfGenerating}
              />
            </View>
            
            {/* Show status if PDF is generating */}
            {pdfGenerating && (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <ActivityIndicator size="small" color={theme.colors.primary} />
                <Text style={{ fontSize: 14, color: theme.colors.gray600 }}>
                  Generating PDF...
                </Text>
              </View>
            )}
          </View>
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.gray50,
  },
  scrollView: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background,
  },
  loadingText: {
    marginTop: theme.spacing.md,
    fontSize: 16,
    color: theme.colors.textSecondary,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background,
    padding: theme.spacing.lg,
  },
  errorText: {
    fontSize: 16,
    color: theme.colors.error,
    textAlign: 'center',
  },
  noDataContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background,
    padding: theme.spacing.lg,
  },
  noDataText: {
    fontSize: 16,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  headerSection: {
    backgroundColor: theme.colors.white,
    borderWidth: 2,
    borderColor: theme.colors.gray400,
    margin: theme.spacing.md,
    marginBottom: theme.spacing.sm,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing.lg,
  },
  headerLeft: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  logoContainer: {
    width: 60,
    height: 60,
    backgroundColor: theme.colors.white,
    borderRadius: 30,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: theme.spacing.md,
    borderWidth: 2,
    borderColor: theme.colors.primary,
    shadowColor: theme.colors.gray900,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 4,
  },
  logoImage: {
    width: 40,
    height: 40,
  },
  companyInfo: {
    flex: 1,
  },
  companyNameAr: {
    fontSize: 16,
    fontWeight: 'bold' as any,
    color: theme.colors.textPrimary,
    textAlign: 'left',
  },
  companyNameEn: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    textAlign: 'left',
    marginTop: 2,
  },
  headerCenter: {
    flex: 1,
    alignItems: 'center',
  },
  headerRight: {
    flex: 1,
    alignItems: 'flex-end',
  },
  typeAr: {
    fontSize: 18,
    fontWeight: 'bold' as any,
    color: theme.colors.primary,
    textAlign: 'center',
  },
  typeEn: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    marginTop: 4,
  },
  section: {
    margin: theme.spacing.md,
    marginBottom: theme.spacing.sm,
  },
  basicInfoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.md,
  },
  inputGroup: {
    flex: 1,
    minWidth: 200,
  },
  labelText: {
    fontSize: 14,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.xs,
    textAlign: 'right',
  },
  readOnlyInput: {
    borderWidth: 1,
    borderColor: theme.colors.gray300,
    borderRadius: 8,
    padding: theme.spacing.sm,
    backgroundColor: theme.colors.gray100,
    minHeight: 44,
    justifyContent: 'center',
  },
  readOnlyText: {
    fontSize: 14,
    color: theme.colors.textPrimary,
    textAlign: 'right',
  },
  sectionHeader: {
    backgroundColor: theme.colors.gray100,
    padding: theme.spacing.md,
    alignItems: 'center',
    marginBottom: theme.spacing.md,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold' as any,
    color: theme.colors.textPrimary,
    textAlign: 'center',
  },
  receivingContent: {
    flexDirection: 'row',
    gap: theme.spacing.lg,
  },
  leftColumn: {
    flex: 1,
  },
  rightColumn: {
    flex: 1,
  },
  diagramContainer: {
    borderWidth: 1,
    borderColor: theme.colors.gray300,
    backgroundColor: theme.colors.gray50,
    padding: theme.spacing.sm,
    borderRadius: 8,
    marginBottom: theme.spacing.sm,
  },
  diagramTitle: {
    fontSize: 14,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    textAlign: 'center',
    marginBottom: theme.spacing.sm,
  },
  vehicleDiagram: {
    alignItems: 'center',
  },
  imageContainer: {
    position: 'relative',
    alignItems: 'center',
  },
  touchableImage: {
    position: 'relative',
  },
  teslaImage: {
    width: 350,
    height: 250,
    marginVertical: theme.spacing.xs,
  },
  svgOverlay: {
    position: 'absolute',
    top: theme.spacing.xs,
    left: 0,
    width: 350,
    height: 250,
    zIndex: 1,
  },
  instructionText: {
    fontSize: 11,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    marginTop: theme.spacing.sm,
    fontStyle: 'italic' as any,
  },
  damageList: {
    marginTop: theme.spacing.md,
    padding: theme.spacing.sm,
    backgroundColor: theme.colors.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.gray300,
  },
  damageListTitle: {
    fontSize: 14,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.sm,
    textAlign: 'center',
  },
  damageItem: {
    marginBottom: theme.spacing.xs,
  },
  damageItemHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  damageNumber: {
    width: 24,
    height: 24,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  damageNumberText: {
    color: theme.colors.white,
    fontSize: 12,
    fontWeight: 'bold' as any,
  },
  damageItemText: {
    fontSize: 12,
    color: theme.colors.textPrimary,
    fontWeight: '500' as any,
  },
  damageInfo: {
    flex: 1,
    marginHorizontal: theme.spacing.sm,
  },
  damageDescription: {
    fontSize: 11,
    color: theme.colors.textSecondary,
    marginTop: 2,
    fontStyle: 'italic' as any,
  },
  attachmentIndicator: {
    marginTop: theme.spacing.sm,
    padding: theme.spacing.sm,
    backgroundColor: theme.colors.gray100,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: theme.colors.gray300,
  },
  attachmentText: {
    fontSize: 13,
    color: theme.colors.textSecondary,
    fontStyle: 'italic' as any,
  },
  notesContainer: {
    marginTop: theme.spacing.md,
  },
  notesLabel: {
    fontSize: 14,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.sm,
    textAlign: 'center',
  },
  notesDisplay: {
    borderWidth: 1,
    borderColor: theme.colors.gray300,
    borderRadius: 8,
    padding: theme.spacing.sm,
    backgroundColor: theme.colors.gray100,
    minHeight: 80,
  },
  notesText: {
    fontSize: 14,
    color: theme.colors.textPrimary,
    textAlign: 'right',
    lineHeight: 20,
  },
  dateTimeRow: {
    flexDirection: 'row',
    gap: theme.spacing.md,
    marginBottom: theme.spacing.md,
  },
  conditionsContainer: {
    gap: theme.spacing.sm,
  },
  conditionsTitle: {
    fontSize: 16,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
    textAlign: 'center',
  },
  conditionRow: {
    flexDirection: 'row',
    gap: theme.spacing.md,
    marginBottom: theme.spacing.sm,
  },
  conditionItem: {
    flex: 1,
  },
  conditionLabel: {
    fontSize: 12,
    color: theme.colors.textPrimary,
    textAlign: 'right',
    marginBottom: theme.spacing.xs,
    fontWeight: '500' as any,
  },
  conditionDisplay: {
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.xs,
    borderWidth: 1,
    borderColor: theme.colors.gray300,
    borderRadius: 4,
    backgroundColor: theme.colors.gray100,
    justifyContent: 'center',
    alignItems: 'center',
  },
  conditionDisplayText: {
    fontSize: 10,
    color: theme.colors.textPrimary,
    textAlign: 'center',
  },
  signatureSection: {
    margin: theme.spacing.md,
  },
  signatureRow: {
    flexDirection: 'row',
    gap: theme.spacing.md,
  },
  signatureCard: {
    flex: 1,
  },
  signatureHeader: {
    backgroundColor: theme.colors.primary,
    padding: theme.spacing.md,
    borderRadius: 8,
    marginBottom: theme.spacing.md,
  },
  signatureTitle: {
    fontSize: 14,
    fontWeight: '600' as any,
    color: theme.colors.white,
    textAlign: 'center',
  },
  signatureContent: {
    gap: theme.spacing.md,
  },
  signatureBox: {
    height: 60,
    borderWidth: 2,
    borderColor: theme.colors.gray300,
    borderRadius: 8,
    backgroundColor: theme.colors.gray50,
    justifyContent: 'center',
    alignItems: 'center',
  },
  signaturePlaceholder: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  printButtonContainer: {
    padding: theme.spacing.md,
    backgroundColor: theme.colors.background,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
  },
  printButton: {
    backgroundColor: theme.colors.primary,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  printButtonText: {
    color: theme.colors.white,
    fontSize: 16,
    fontWeight: '600' as any,
  },
  printUnavailable: {
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.sm,
    backgroundColor: theme.colors.gray100,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.gray300,
  },
  printUnavailableText: {
    textAlign: 'center',
    color: theme.colors.textSecondary,
    fontSize: 14,
  },
});